import { useEffect, useMemo } from 'react';

import { ArtificialIntelligence08Icon, BubbleChatQuestionIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { FormTypeDetails, FormTypes } from 'constants/forms';
import { useSubmitProgress } from 'hooks/use-submit-progress';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import {
  Button,
  Input,
  InputGroup,
  RichTextEditor,
  Select,
  Textarea,
  Toggle,
} from 'components/FormComponents';
import { FileContentsPicker } from 'components/FormComponents/FileContentsPicker/FileContentsPicker';
import { SubmitProgressBar } from 'components/SubmitProgressBar/SubmitProgressBar';

import { selectCompanyChecks } from 'api/company-checks-slice';

export const CreateQuestionnaireModal = ({
  visible,
  onClose,
  onSubmit,
  isSubmitting = false,
  teams = [],
  showTeamSelect = false,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm();

  const watchType = watch('type');
  const watchCreateFrom = watch('create_from');
  const watchFileData = watch('file_data');
  const watchTeamId = watch('team_id');

  const { progress, status } = useSubmitProgress(isSubmitting, { resetWhen: visible });

  const companyChecks = useSelector(selectCompanyChecks);

  const hasCandidateQuestionnaires = useMemo(() => {
    return (companyChecks || []).some(check => check.type === 'candidate_questionnaire');
  }, [companyChecks]);

  const hasCustomReference = useMemo(() => {
    return (companyChecks || []).some(check => check.type === 'custom_reference');
  }, [companyChecks]);

  useEffect(() => {
    if (!visible) reset();
  }, [visible, reset]);

  const handleFileUpload = fileData => {
    setValue('file_data', fileData);
  };

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      title="New Questionnaire Settings"
      description="Set your questionnaire name and description."
      icon={BubbleChatQuestionIcon}
      oldIconLibrary={false}
      small>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-2">
        {/* Questionnaire Type */}
        <InputGroup title="Type">
          <Select
            name="type"
            placeholder="Select Type..."
            register={register}
            validators={{ required: true }}
            value={watchType}
            error={errors?.type?.type}>
            {hasCandidateQuestionnaires && (
              <Select.Item value={FormTypes.QUESTIONNAIRE}>
                {FormTypeDetails[FormTypes.QUESTIONNAIRE].label}
              </Select.Item>
            )}
            {hasCustomReference && (
              <Select.Item value={FormTypes.CANDIDATE}>
                {FormTypeDetails[FormTypes.CANDIDATE].label}
              </Select.Item>
            )}
            {hasCustomReference && (
              <Select.Item value={FormTypes.REFEREE}>
                {FormTypeDetails[FormTypes.REFEREE].label}
              </Select.Item>
            )}
          </Select>
        </InputGroup>

        {/* Questionnaire Name */}
        <InputGroup title="Name">
          <Input
            name="name"
            placeholder="I.e. Marketing Manager Reference Check..."
            register={register}
            validators={{ required: true }}
            error={errors?.name?.type}
          />
        </InputGroup>

        {/* Questionnaire Description */}
        <InputGroup title="Description" optional>
          <Textarea
            name="description"
            placeholder="Provide a brief summary of the questionnaire's purpose..."
            register={register}
            rows={3}
          />
        </InputGroup>

        {/* Team */}
        {showTeamSelect && (
          <InputGroup title="Team">
            <Select
              name="team_id"
              placeholder="Team"
              register={register}
              value={watchTeamId}
              setValue={setValue}>
              <Select.Item value={null}>Global</Select.Item>
              {teams.map(team => (
                <Select.Item key={team.id} value={team.id}>
                  {team.name}
                </Select.Item>
              ))}
            </Select>
          </InputGroup>
        )}

        {/* Message */}
        <InputGroup
          title="Message"
          optional
          description="This message will be shown to the candidate or referee on their experience.">
          <Controller
            name="message"
            control={control}
            render={({ field: { value, onChange } }) => (
              <RichTextEditor value={value} onChange={onChange} />
            )}
          />
        </InputGroup>

        {/* Create using AI */}
        {watchType === FormTypes.REFEREE && (
          <div className="flex flex-col gap-2">
            <div className="flex items-start justify-between">
              <div className="flex flex-col gap-2 mb-6 w-3/4">
                <span className="text-2xl text-neutral-7 font-medium flex items-center gap-2">
                  <HugeiconsIcon
                    icon={ArtificialIntelligence08Icon}
                    size={20}
                    strokeWidth="2"
                    className="text-primary-4"
                  />{' '}
                  Generate with AI
                </span>
                <span className="text-xl text-neutral-6">
                  This feature helps you create reference check questions automatically. Based on
                  the job description or questionnaire you provide, we generate relevant questions
                  that make it easy to gather structured feedback from referees.{' '}
                </span>
              </div>
              <Toggle register={register} name="use_ai" />
            </div>
            {watch('use_ai') && (
              <>
                <InputGroup title="Select your source">
                  <Select
                    name="create_from"
                    placeholder="Select Source..."
                    register={register}
                    value={watchCreateFrom}
                    setValue={setValue}
                    error={errors?.create_from?.type}>
                    <Select.Item value="job_description">Job Description</Select.Item>
                    <Select.Item value="questionnaire">Questionnaire</Select.Item>
                  </Select>
                </InputGroup>

                <InputGroup title="Upload your document">
                  <div className="flex flex-col gap-2">
                    <span className="text-xl text-neutral-6">
                      Upload a PDF containing a questionnaire or job description. The more detailed
                      the document, the better we can generate relevant and intelligent questions
                      for your form.{' '}
                    </span>
                    <FileContentsPicker
                      accept={{
                        'application/pdf': ['.pdf'],
                        'application/msword': ['.doc', '.docx'],
                      }}
                      onChange={handleFileUpload}
                    />
                  </div>
                </InputGroup>
              </>
            )}
          </div>
        )}

        <SubmitProgressBar
          progress={progress}
          status={status}
          loadingLabel="We are intelligently creating your questionnaire..."
          completingLabel="Your questionnaire is almost ready to be shipped!"
          className="mb-2"
        />

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button type="secondary" onClick={onClose}>
            Close
          </Button>
          <Button submit disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Questionnaire'}
          </Button>
        </div>
      </form>
    </AnimatedModal>
  );
};
